import {
  allClients, allProjects, allInvoices, allEnquiries, allUsers, ACTIVE_STATUSES, asDate,
  updateClient, listFeedback, addRecordChange, allInstructionsIndex,
  clientInsights, clientActionTasks, createTask, updateTask, setTaskState, emailsForNumbers, orgSettings,
} from '../db.js';
import { deleteField } from '../fb/firestore.js';
import { escapeHtml, refTail, money, fmtDate, skeleton, secretFig, CLIENT_SCHEMES, schemeLabel } from '../ui.js';
import { openOverlay } from './overlay.js';
import { attachPlacesAutocomplete } from '../places-ui.js';
import { mark } from '../marks.js';
import { openEndingConfirm } from './ending-confirm.js';
import { injectFormStyles } from './forms.js';
import { headerWhere, headerBack } from '../header.js';
import { hasFeature } from '../features.js';
import {
  STAGES, stageIndex, toDate, invoicesCurrent, termsDays, indexByClient, buildClient, sortClients, sortLabel,
  matches, clientBrief, whyHere, CLIENT_SEGMENTS, suggestedActions, contactLabel, isLive, contactsFromMail,
} from '../client-insights.js';

// Clients. The list and the client page follow design_handoff_clients
// (18/10/2026, below). The relationship CARD (artboard 6b, mountClientCard)
// stays as the deep editor behind "Edit client" and openClientCard, which
// other screens open over themselves. Billed £ is visible ONLY to
// Owner / Director / Finance (Karan, 06/07). Every value is escaped through
// escapeHtml (house rule 0).

const FINANCE = ['Owner', 'Director', 'Finance'];

// --- Shared inline-style fragments (artboard tokens) ------------------------
const CAPS = 'font-size:10px;font-weight:500;letter-spacing:0.14em;text-transform:uppercase';
const CAPS_GREY = `${CAPS};color:var(--ink-3)`;
const CAPS_ORANGE = `${CAPS};color:var(--brand-orange)`;
const CAPS_BLUE = `${CAPS};color:var(--link)`;
const MONO = 'font-family:var(--font-technical)';
const FIELD_BOX = 'width:100%;box-sizing:border-box;border:1px solid var(--hair);border-radius:8px;'
  + 'padding:11px 13px;font-size:13.5px;color:var(--ink);font-family:inherit;background:var(--surface)';
const THREAD_ROW = 'display:grid;grid-template-columns:82px 1fr auto;gap:14px;align-items:center;'
  + 'padding:10px 0;border-bottom:1px solid var(--hair)';
const NONE_NOTE = 'font-size:12px;color:var(--ink-3);margin:6px 0 0';

// Scheme (was "Type") — a fixed set of project schemes (Karan 17/07). The client's
// scheme denormalises onto their projects for the dashboard list. Any legacy value
// that isn't one of these is preserved as an extra option so a save can't drop it.
// A select drawn like FIELD_BOX, with its own chevron (Scheme, Payment terms).
const SELECT_BOX = `${FIELD_BOX};appearance:none;-webkit-appearance:none;cursor:pointer;`
  + `background-image:url('data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%2210%22 height=%2210%22 viewBox=%220 0 10 10%22><path d=%22M1 3l4 4 4-4%22 stroke=%22%238A8B85%22 stroke-width=%221.4%22 fill=%22none%22 stroke-linecap=%22round%22/></svg>');`
  + 'background-repeat:no-repeat;background-position:right 13px center';
function schemeSelect(current, disabledAttr) {
  const cur = (current || '').trim();
  const opts = CLIENT_SCHEMES.slice();
  if (cur && !opts.some((o) => o.toLowerCase() === cur.toLowerCase())) opts.push(cur);
  return `<select id="c-type" ${disabledAttr} style="${SELECT_BOX}">`
    + `<option value=""${cur ? '' : ' selected'}>— Select scheme —</option>`
    + opts.map((o) => `<option value="${escapeHtml(o)}"${o.toLowerCase() === cur.toLowerCase() ? ' selected' : ''}>${escapeHtml(schemeLabel(o))}</option>`).join('')
    + `</select>`;
}

// Normalise a client name for duplicate detection.
const normName = (s) => String(s || '').toLowerCase()
  .replace(/\b(ltd|limited|llp|plc|uk|the|co|company)\b/g, '')
  .replace(/[^a-z0-9]/g, '');

function similarTo(client, clients) {
  const n = normName(client.name);
  if (n.length < 4) return [];
  return clients.filter((c) => {
    if (c.id === client.id) return false;
    const m = normName(c.name);
    if (m.length < 4) return false;
    return m === n || m.includes(n) || n.includes(m);
  });
}

// --- Golden-thread status colours (artboard 6b) -----------------------------
// The card renders status as plain coloured text (no dot). Active project work
// reads blue, done work greys out; enquiries/invoices/feedback follow the
// artboard's readings — Won/Paid/Positive green, Lost grey, unpaid orange.
const projStatusColor = (s) => (ACTIVE_STATUSES.includes(s) ? 'var(--link)' : 'var(--ink-3)');   // text: --link is --brand-blue in light and flips in dark
const ENQ_COLOR = {
  Won: 'var(--status-ok)', Lost: 'var(--hair-strong)', Quoted: 'var(--attn-ink)',
  Enquiry: 'var(--link)', Draft: 'var(--ink-3)',
};
const enqStatusColor = (s) => ENQ_COLOR[s] || 'var(--ink-3)';

// One golden-thread row — a project/enquiry (clickable button) or an
// invoice/feedback (static div). `nav` carries the data-attr that wires the
// onOpenProject / onOpenEnquiry callbacks in mountClientCard.
function threadRow({ nav = '', no, desc, status, color }) {
  const inner = `<span style="${MONO};font-size:12.5px;font-weight:500">${escapeHtml(no)}</span>`
    + `<span style="font-size:13px;min-width:0;white-space:nowrap;overflow:hidden;text-overflow:ellipsis">${escapeHtml(desc)}</span>`
    + `<span style="font-size:12px;color:${color}">${escapeHtml(status)}</span>`;
  if (nav) {
    return `<button ${nav} class="gt-nav" style="${THREAD_ROW};cursor:pointer;background:none;border:none;`
      + `border-bottom:1px solid var(--hair);width:100%;text-align:left;font-family:inherit;color:inherit">${inner}</button>`;
  }
  return `<div style="${THREAD_ROW};cursor:default">${inner}</div>`;
}

// A golden-thread section — mark + blue caps title + navy rule + count, then rows.
// markName null (Feedback) draws no mark, per the artboard. `countAttr` lets the
// Feedback count fill in lazily.
function threadSection(markName, title, count, inner, countAttr = '') {
  return `<div style="display:flex;flex-direction:column;gap:4px">
    <div style="display:flex;align-items:center;gap:10px;padding-bottom:8px;border-bottom:1px solid var(--ink)">
      ${markName ? mark(markName, { size: 17 }) : ''}
      <span style="${CAPS_BLUE}">${escapeHtml(title)}</span>
      <div style="flex:1"></div>
      <span ${countAttr} style="${MONO};font-size:12px;color:var(--ink-3)">${count == null ? '' : escapeHtml(String(count))}</span>
    </div>
    ${inner}
  </div>`;
}

// The golden-thread markup — a client's projects / enquiries / invoices, linked
// ("no dead ends"). Pure (takes related records + role flags) so it renders
// identically on the Clients page and in the standalone client card.
function goldenThreadHtml(related, flags) {
  const projects = related.projects.slice().sort((a, b) => String(b.number || '').localeCompare(String(a.number || '')));
  const enquiries = related.enquiries.slice().sort((a, b) => (asDate(b.receivedAt) || 0) - (asDate(a.receivedAt) || 0));
  const invoices = related.invoices.slice().sort((a, b) => String(b.number || '').localeCompare(String(a.number || '')));

  const projRows = projects.slice(0, 12).map((p) => threadRow({
    nav: `data-gt-project="${escapeHtml(p.id)}"`,
    no: p.number || '—', desc: p.title || p.service || '', status: p.status || '—', color: projStatusColor(p.status),
  })).join('') || `<p style="${NONE_NOTE}">None.</p>`;

  const enqRows = enquiries.slice(0, 12).map((e) => threadRow({
    nav: `data-gt-enquiry="${escapeHtml(e.id)}"`,
    no: e.number || 'NEW', desc: e.subject || e.description || '', status: e.status || '—', color: enqStatusColor(e.status),
  })).join('') || `<p style="${NONE_NOTE}">None.</p>`;

  const invRows = invoices.slice(0, 12).map((iv) => {
    const amt = money(Number(iv.amount) || 0);
    const desc = [iv.description || '', amt].filter(Boolean).join(' · ');
    return threadRow({
      no: String(iv.number || '—'), desc, status: iv.paymentStatus || '—',
      color: iv.paymentStatus === 'Paid' ? 'var(--status-ok)' : 'var(--brand-orange)',
    });
  }).join('') || `<p style="${NONE_NOTE}">None.</p>`;

  // Feedback fills in lazily (mountClientCard fetches it) — see #cd-feedback.
  const fbSec = threadSection(null, 'Feedback', null,
    `<div id="cd-feedback"><p style="${NONE_NOTE}">Loading…</p></div>`, 'id="cd-feedback-count"');

  return `<div style="display:flex;flex-direction:column;gap:24px;padding-top:24px;border-top:1px solid var(--hair)">
    ${threadSection('instruction', 'Projects', projects.length, projRows)}
    ${flags.canSeeEnquiries ? threadSection('golden-thread', 'Enquiries', enquiries.length, enqRows) : ''}
    ${flags.isFinance ? threadSection('invoice', 'Invoices', invoices.length, invRows) : ''}
    ${fbSec}
  </div>`;
}

// Render + wire the client card into an already-open overlay. Shared by the
// Clients page and openClientCard(), so a golden-thread pop-up looks and behaves
// the same wherever it's opened. Matches artboard 6b.
function mountClientCard(content, close, c, related, flags, handlers, allClientList) {
  const { session, onOpenProject, onOpenEnquiry } = handlers || {};
  const clientList = allClientList || [];
  const relatedIds = Array.isArray(c.relatedClientIds) ? c.relatedClientIds : [];
  const relatedClients = relatedIds.map((id) => clientList.find((x) => x.id === id)).filter(Boolean);
  const linkTargets = clientList.filter((x) => x.id !== c.id && !relatedIds.includes(x.id));
  const norm = (s) => String(s || '').trim().toLowerCase();
  const remount = () => mountClientCard(content, close, c, related, flags, handlers, clientList);
  const relinkBoth = async (targetId, add) => {
    const target = clientList.find((x) => x.id === targetId); if (!target) return;
    const uniq = (a) => [...new Set(a)];
    const aRel = add ? uniq([...relatedIds, targetId]) : relatedIds.filter((x) => x !== targetId);
    const tRel0 = Array.isArray(target.relatedClientIds) ? target.relatedClientIds : [];
    const tRel = add ? uniq([...tRel0, c.id]) : tRel0.filter((x) => x !== c.id);
    const stamp = { updatedBy: session ? session.email : null, updatedAt: new Date().toISOString() };
    await Promise.all([
      updateClient(c.id, { relatedClientIds: aRel, ...stamp }),
      updateClient(targetId, { relatedClientIds: tRel, ...stamp }),
    ]);
    c.relatedClientIds = aRel; target.relatedClientIds = tRel; // keep local cache in step
    remount();
  };
  const isFinance = flags.isFinance;
  const now = Date.now();
  const projDates = related.projects.map((p) => asDate(p.createdAt)).filter((d) => d && d.getTime() <= now);
  const lastSafe = projDates.length ? new Date(Math.max(...projDates.map((d) => d.getTime()))) : (c.last && c.last.getTime() <= now ? c.last : null);
  const emails = Array.isArray(c.emails) ? c.emails.join(', ') : (c.emails || '');
  const canEdit = !!(session && ['Owner', 'Director', 'Finance', 'Coordinator'].some((r) => (session.roles || []).includes(r)));
  const ro = canEdit ? '' : 'disabled';
  // Payment terms (v4.26.7): billing, so Owner / Director / Finance see and set
  // them (firestore.rules holds the same line). On receipt = 0; Company default
  // clears the client's own, so the invoice uses Settings › Money; Custom = days.
  const termsEditor = !!(session && FINANCE.some((r) => (session.roles || []).includes(r)));
  const defaultDays = termsDays({}, flags.defaultTerms);
  const own = c.terms != null || c.paymentTerms ? termsDays({ terms: c.terms, paymentTerms: c.paymentTerms }, null) : null;
  const termsMode = own == null ? 'default' : own === 0 ? 'receipt' : 'custom';
  const roT = termsEditor ? '' : 'disabled';
  const termsHtml = `<div style="display:flex;gap:9px;align-items:center">
      <select id="c-terms-mode" aria-label="Payment terms" ${roT} style="${SELECT_BOX};flex:1">
        ${[['receipt', 'On receipt'], ['default', `Company default, ${defaultDays === 0 ? 'on receipt' : `${defaultDays} days`}`], ['custom', 'Custom']]
          .map(([v, l]) => `<option value="${v}"${v === termsMode ? ' selected' : ''}>${escapeHtml(l)}</option>`).join('')}
      </select>
      <span id="c-terms-custom" style="display:${termsMode === 'custom' ? 'flex' : 'none'};align-items:center;gap:7px">
        <input id="c-terms-days" inputmode="numeric" aria-label="Payment terms, days" value="${escapeHtml(termsMode === 'custom' ? String(own) : '')}" placeholder="30" ${roT} style="${FIELD_BOX};width:84px">
        <span style="font-size:13px;color:var(--ink-3)">days</span>
      </span>
    </div>`;

  // Figures row (artboard 6b): Projects / Live / Billed (Finance only) / Last project.
  const billedStr = c.billed ? money(c.billed) : '—';
  const figures = [
    { value: String(c.projects || 0), label: 'Projects' },
    { value: String(c.live || 0), label: 'Live' },
    ...(isFinance ? [{ value: secretFig(escapeHtml(billedStr), true), label: 'Billed', html: true }] : []),
    { value: lastSafe ? fmtDate(lastSafe) : '—', label: 'Last project' },
  ];
  const figuresHtml = `<div style="display:grid;grid-template-columns:repeat(${figures.length},1fr);gap:24px">
    ${figures.map((f) => `<div style="display:flex;flex-direction:column;gap:5px">
      <span style="${CAPS_GREY}">${escapeHtml(f.label)}</span>
      <span style="font-size:30px;font-weight:500;line-height:1;letter-spacing:-0.03em">${f.html ? f.value : escapeHtml(f.value)}</span>
    </div>`).join('')}
  </div>`;

  const field = (label, inputHtml, full) => `<div style="display:flex;flex-direction:column;gap:7px${full ? ';grid-column:1/-1' : ''}">
    <span style="${CAPS_GREY}">${escapeHtml(label)}</span>${inputHtml}</div>`;
  const input = (id, value, extra = '') => `<input id="${id}" value="${escapeHtml(value)}" ${ro} style="${FIELD_BOX}" ${extra}>`;

  const dupBrief = c.similar && c.similar.length ? `<div style="background:var(--brief-bg);border-radius:16px;padding:20px 22px">
    <div style="${CAPS_ORANGE};margin-bottom:10px">Possible duplicate</div>
    <div style="font-size:15px;line-height:1.55;text-wrap:pretty">${escapeHtml(`This record looks like a possible duplicate of ${c.similar.map((s) => s.name).join(' and ')} — worth merging so this client's projects, enquiries and invoices don't fork across two files.`)}</div>
  </div>` : '';

  const relatedChips = relatedClients.map((rc) => `<span style="display:flex;align-items:center;gap:9px;border:1px solid var(--hair);border-radius:999px;padding:7px 9px 7px 14px;font-size:12.5px">
      <button data-open-client="${escapeHtml(rc.id)}" style="background:none;border:none;padding:0;font:inherit;color:var(--ink);cursor:pointer">${escapeHtml(rc.name || '—')}</button>
      ${canEdit ? `<button data-unlink="${escapeHtml(rc.id)}" title="Unlink" style="background:none;border:none;padding:0;font:inherit;color:var(--ink-3);cursor:pointer">×</button>` : ''}
    </span>`).join('')
    || `<span style="${NONE_NOTE};margin:0">None linked — tag firms that belong to the same group.</span>`;

  const relatedAdd = canEdit ? `<div style="display:flex;align-items:center;gap:9px">
      <input id="c-related-pick" list="c-related-list" placeholder="Link a related client…" autocomplete="off"
        style="border:1px solid var(--hair);border-radius:8px;padding:8px 14px;font-size:12.5px;color:var(--ink);font-family:inherit;background:var(--surface);min-width:220px">
      <datalist id="c-related-list">${linkTargets.map((o) => `<option value="${escapeHtml(o.name || '')}">`).join('')}</datalist>
      <button id="c-related-add" style="border:1px solid var(--hair);border-radius:8px;padding:8px 14px;font-size:12.5px;color:var(--ink);background:var(--surface);cursor:pointer;font-family:inherit;white-space:nowrap">+ Link</button>
    </div>` : '';

  const foot = `<div style="display:flex;align-items:center;gap:11px;padding-top:24px;border-top:1px solid var(--hair)">
    <span style="font-size:12px;color:var(--ink-3)">${escapeHtml(canEdit ? 'Manual edits here are kept — the archiver will not overwrite them.' : 'You can view this client. Editing is for Coordinator and above.')}</span>
    <div style="flex:1"></div>
    <button id="c-cancel" style="border:1px solid var(--hair);border-radius:8px;padding:10px 17px;font-size:13px;color:var(--ink);background:var(--surface);cursor:pointer;font-family:inherit">Close</button>
    ${canEdit ? '<button id="c-save" style="background:var(--ink-fill);color:var(--ink-fill-ink);border:none;border-radius:8px;padding:10px 19px;font-size:13px;font-weight:500;cursor:pointer;font-family:inherit">Save</button>' : ''}
  </div>`;

  content.innerHTML = `
    <div style="max-width:940px;margin:0 auto;padding:28px 30px 40px;display:flex;flex-direction:column;gap:26px">
      ${dupBrief}
      ${figuresHtml}
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:18px 22px;padding-top:24px;border-top:1px solid var(--hair)">
        ${field('Client name', input('c-name', c.name || ''))}
        ${field('Scheme', schemeSelect(c.type, ro))}
        ${field('Contact name', input('c-contact', c.contactName || ''))}
        ${field('Phone', input('c-number', c.contactNumber || ''))}
        ${isFinance ? field('Payment terms · printed on invoices', termsHtml) : ''}
        ${field('Emails', input('c-emails', emails, 'placeholder="comma-separated"'), true)}
        ${field('Address', `<div class="fc-picker"><input id="c-address" value="${escapeHtml(c.address || '')}" placeholder="${canEdit ? 'Start typing to search…' : ''}" autocomplete="off" ${ro} style="${FIELD_BOX}"><div class="fc-dropdown" id="c-addr-drop" hidden></div></div>`, true)}
        ${field('Additional contacts', input('c-extra', c.v2Contacts || '', 'placeholder="e.g. Site manager Dave — 07700 900000"'), true)}
        ${field('Notes', `<textarea id="c-notes" placeholder="Anything the team should know about this client…" ${ro} style="${FIELD_BOX};min-height:62px;line-height:1.5;resize:vertical">${escapeHtml(c.notes || '')}</textarea>`, true)}
      </div>
      ${goldenThreadHtml(related, flags)}
      <div style="display:flex;flex-direction:column;gap:12px;padding-top:24px;border-top:1px solid var(--hair)">
        <span style="${CAPS_BLUE}">Related clients</span>
        <div style="display:flex;align-items:center;gap:9px;flex-wrap:wrap">${relatedChips}${relatedAdd}</div>
      </div>
      <div id="c-err" style="color:var(--status-error);font-size:13px"></div>
      ${foot}
    </div>`;

  content.querySelectorAll('[data-gt-project]').forEach((el) => el.addEventListener('click', () => {
    if (onOpenProject) { close(); onOpenProject(el.dataset.gtProject); }
  }));
  content.querySelectorAll('[data-gt-enquiry]').forEach((el) => el.addEventListener('click', () => {
    if (onOpenEnquiry) { close(); onOpenEnquiry(el.dataset.gtEnquiry); }
  }));
  content.querySelector('#c-cancel').addEventListener('click', close);

  // Golden-thread feedback (Karan 17/07 — "see it in projects, and in clients"):
  // this client's feedback/complaints, keyed on their project numbers. Lazy-loaded
  // like the project page's feedback section; read-only here.
  const fbEl = content.querySelector('#cd-feedback');
  if (fbEl) {
    const projNos = new Set(related.projects.map((p) => String(p.number)).filter(Boolean));
    const fbColor = (f) => (f.positive ? 'var(--status-ok)' : (f.status === 'Resolved' ? 'var(--status-ok)' : (f.status === 'Open' ? 'var(--status-error)' : 'var(--link)')));
    listFeedback().then((all) => {
      const mine = all
        .filter((f) => !f.needsReview && f.status !== 'Dismissed' && projNos.has(String(f.projectNo)))
        .sort((a, b) => (asDate(b.createdAt) || 0) - (asDate(a.createdAt) || 0));
      const cnt = content.querySelector('#cd-feedback-count'); if (cnt) cnt.textContent = mine.length || '';
      fbEl.innerHTML = mine.length ? mine.slice(0, 12).map((f) => threadRow({
        no: f.projectNo || '—', desc: (f.text || '').slice(0, 90),
        status: f.positive ? 'Positive' : (f.status || 'Feedback'), color: fbColor(f),
      })).join('') : `<p style="${NONE_NOTE}">None.</p>`;
    }).catch(() => { fbEl.innerHTML = `<p style="${NONE_NOTE};color:var(--status-error)">Couldn’t load feedback.</p>`; });
  }

  // Related clients: open a chip, unlink a chip, or link a new one (symmetric).
  content.querySelectorAll('[data-open-client]').forEach((el) => el.addEventListener('click', () => {
    close(); openClientCard(el.dataset.openClient, { session, onOpenProject, onOpenEnquiry });
  }));
  content.querySelectorAll('[data-unlink]').forEach((el) => el.addEventListener('click', () => relinkBoth(el.dataset.unlink, false).catch((e) => alert(e.message || e))));
  const addRelBtn = content.querySelector('#c-related-add');
  if (addRelBtn) addRelBtn.addEventListener('click', () => {
    const val = norm(content.querySelector('#c-related-pick').value);
    if (!val) return;
    const target = linkTargets.find((o) => norm(o.name) === val);
    if (!target) { alert('Pick an existing client from the list.'); return; }
    addRelBtn.disabled = true;
    relinkBoth(target.id, true).catch((e) => { addRelBtn.disabled = false; alert(e.message || e); });
  });

  // Google Places address autocomplete (Karan 23/07) — editors only. The shared
  // helper owns the debounce/session-token/geocode logic; we just read its meta on save.
  let addrMeta = { lat: c.addressLat ?? null, lng: c.addressLng ?? null, placeId: c.placeId ?? null };
  if (canEdit) {
    addrMeta = attachPlacesAutocomplete(content.querySelector('#c-address'), content.querySelector('#c-addr-drop'), { initial: addrMeta }).meta;
  }

  const termsSel = content.querySelector('#c-terms-mode');
  if (termsSel) termsSel.addEventListener('change', () => {
    content.querySelector('#c-terms-custom').style.display = termsSel.value === 'custom' ? 'flex' : 'none';
    if (termsSel.value === 'custom') content.querySelector('#c-terms-days').focus();
  });

  const saveBtn = content.querySelector('#c-save');
  if (saveBtn) saveBtn.addEventListener('click', async () => {
    saveBtn.disabled = true; saveBtn.textContent = 'Saving…';
    try {
      const val = (s) => content.querySelector(s).value.trim();
      const emailList = val('#c-emails').split(',').map((e) => e.trim()).filter(Boolean);
      const fields = {
        name: val('#c-name') || c.name, // never blank the name
        type: val('#c-type') || null,
        contactName: val('#c-contact') || null,
        contactNumber: val('#c-number') || null,
        emails: emailList,
        address: val('#c-address') || null,
        addressLat: addrMeta.lat, addressLng: addrMeta.lng, placeId: addrMeta.placeId,
        notes: val('#c-notes') || null,
        v2Contacts: val('#c-extra') || null,
        manualOverride: true,
        updatedBy: session ? session.email : null,
        updatedAt: new Date().toISOString(),
      };
      // Payment terms: written only when an Owner / Director / Finance changed them.
      let termsNext;
      if (termsSel && termsEditor) {
        const mode = termsSel.value;
        if (mode === 'custom') {
          const raw = val('#c-terms-days');
          const n = Number(raw);
          if (!/^\d{1,3}$/.test(raw) || n > 120) throw new Error('Payment terms are a whole number of days, 0 to 120.');
          termsNext = n;
        } else termsNext = mode === 'receipt' ? 0 : null;
        const termsNow = c.terms == null ? null : Number.isInteger(c.terms) ? c.terms : NaN; // NaN: an odd stored value is always rewritten
        if (termsNext !== termsNow) fields.terms = termsNext == null ? deleteField() : termsNext;
        // An old free-text value would still win over the company default, so it goes too.
        if (termsNext == null && c.paymentTerms) fields.paymentTerms = deleteField();
      }
      await updateClient(c.id, fields);
      Object.assign(c, fields);
      if ('terms' in fields) { if (termsNext == null) delete c.terms; else c.terms = termsNext; }
      if ('paymentTerms' in fields) delete c.paymentTerms;
      saveBtn.textContent = '✓ Saved';
      setTimeout(close, 500);
    } catch (e) { saveBtn.disabled = false; saveBtn.textContent = 'Save'; content.querySelector('#c-err').textContent = e.message || String(e); }
  });
}

// Open a client's relationship card as an OVERLAY over the CURRENT page (Karan
// 13/07): golden-thread client links used to navigate to the Clients page, so
// closing the pop-up stranded you there instead of back where you were. This
// opens the same card without changing the view, so closing simply returns you.
export async function openClientCard(clientId, { session, onOpenProject, onOpenEnquiry } = {}) {
  const isFinance = !!(session && session.roles.some((r) => FINANCE.includes(r)));
  const canSeeEnquiries = !!(session && session.roles.some((r) => ['Owner', 'Director', 'Finance', 'Coordinator'].includes(r)));
  const clients = await allClients().catch(() => []);
  const c0 = clients.find((x) => x.id === clientId);
  if (!c0) return;
  const projects = (await allProjects().catch(() => [])).filter((p) => !p.isTest);
  const invoices = isFinance ? await allInvoices().catch(() => []) : [];
  const enquiries = canSeeEnquiries ? (await allEnquiries().catch(() => [])).filter((e) => !e.isTest) : [];
  // A CLIENT REFERENCE IS ONLY WRITTEN WHEN A JOB IS WON, and most enquiries
  // never are: 219 of them carry a client NAME and no clientRef at all. Matching
  // on the ref alone made a client's own enquiries read "None" on their record —
  // Adela Gazi's A0305 sat there with her name on it (Karan 04/09). Where there
  // is no ref to trust, the name is the next best thing; where there IS one it
  // still wins, so a deliberate link is never overridden by a coincidence.
  const named = (v) => String(v || '').trim().toLowerCase();
  const byName = named(c0.name);
  const mine = (r) => (r.clientRef ? refTail(r.clientRef) === clientId
    : !!byName && named(r.clientName) === byName);
  const related = {
    projects: projects.filter(mine),
    invoices: invoices.filter((iv) => refTail(iv.clientRef) === clientId),
    enquiries: enquiries.filter(mine),
  };
  const lastDates = related.projects.map((p) => asDate(p.createdAt)).filter(Boolean);
  const c = {
    ...c0,
    projects: related.projects.length,
    live: related.projects.filter((p) => ACTIVE_STATUSES.includes(p.status)).length,
    billed: related.invoices.reduce((s, iv) => s + (Number(iv.amount) || 0), 0),
    last: lastDates.length ? new Date(Math.max(...lastDates.map((d) => d.getTime()))) : null,
    similar: similarTo(c0, clients),
  };
  // DIALOGS&FORMS Q: create-vs-edit resolved by existence — `c0` is loaded from
  // allClients above, so this is an EXISTING record: by 7a it can NEVER be a
  // create modal, and if it were a form it would be a panel. But it is NOT a 7a
  // create/edit form: it is the 6b golden-thread relationship CARD (figures +
  // projects/enquiries/invoices/feedback threads + related-client linking + deep
  // inline edit with Places autocomplete) — a wide record viewer, which is what
  // openOverlay exists for. The 520/640 dialog shell cannot hold the 6b layout
  // or the card's several write paths (field save, related-client add/unlink,
  // remount) without losing the golden thread — a regression the shell brief
  // forbids. Like the 6d panels (dialog.js: "migrated separately"), this 6b card
  // is a separate, larger migration, so it stays on openOverlay for now.
  const { content, close } = openOverlay({
    title: c.name || '—',
    subtitle: [schemeLabel(c.type), c.address].filter(Boolean).join(' · '),
    wide: true,
  });
  const st = isFinance ? await orgSettings().catch(() => ({})) : {};
  mountClientCard(content, close, c, related, { canSeeEnquiries, isFinance, defaultTerms: ((st || {}).money || {}).paymentTermsDays ?? null }, { session, onOpenProject, onOpenEnquiry }, clients);
}

// ═════════════════════════════════════════════════════════════════════════════
// CLIENTS LIST + CLIENT DETAIL — design_handoff_clients (18/10/2026).
//
// Built around a director's three jobs: the weekly review (which clients need
// me), chasing money and judging credit risk, and spotting growth. The rules
// (flags, risk, the words Track writes, the suggested actions) live in
// client-insights.js, pure and tested; this file only draws and wires.
//
// MONEY: the Credit risk / Overdue / Days to pay / Billed FY columns, the Money
// margin and the Chase / Protect-by-payment flags appear only to Finance AND
// only while Track holds current invoices (client-insights invoicesCurrent).
// Today it does not (nothing after 7664, 24/06/2026), so Finance see a note
// saying so, and the columns return on their own when invoicing resumes.
//
// RATINGS: the Client Experience Panel has no source in Track yet (18/10), so
// the Rating column reads "—" and the margin "No ratings yet".
//
// The page header and rail are the app's own and are not touched; the record
// header line and back link use header.js like every other record screen.
// ═════════════════════════════════════════════════════════════════════════════

// The list's sort, search and focus survive a re-render or a trip to a client
// and back, for the session.
const LIST_STATE = { sort: { key: 'pri', dir: 1 }, q: '', focus: 0, seg: 'all' };
const SEARCH_ICON = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" aria-hidden="true"><circle cx="11" cy="11" r="6.5"/><path d="M16 16l4.5 4.5"/></svg>';
const sentence = (s) => { const t = String(s || ''); return t ? t[0].toUpperCase() + t.slice(1).toLowerCase() : ''; };

const C = {
  ink: 'var(--ink)', ink2: 'var(--ink-2)', muted: 'var(--ink-3)', grey: 'var(--hair-strong)',
  orange: 'var(--brand-orange)', orangeInk: 'var(--attn-ink)', blue: 'var(--link)', dark: 'var(--anchor)',
};
const EYEBROW = 'font-size:11px;font-weight:500;letter-spacing:0.14em;text-transform:uppercase';
const RISK_STYLE = {
  High: { dot: C.orange, ink: C.orangeInk, w: 500 },
  Elevated: { dot: C.orange, ink: C.orangeInk, w: 500 },
  Watch: { dot: C.dark, ink: C.ink, w: 400 },
  Low: { dot: C.grey, ink: C.muted, w: 400 },
};
const flagColour = (f) => (f === 'Grow' ? C.blue : f ? C.orange : 'transparent');
const amount = (n, allowed) => secretFig(escapeHtml(n ? money(n) : '—'), allowed);

/** Segments (client-insights) → safe HTML. Amounts go through the figures shield. */
function segHtml(segs, allowed) {
  return segs.map((s) => {
    if (s.m != null) return `<span style="color:${s.o ? C.orange : 'inherit'}">${amount(s.m, allowed)}</span>`;
    if (s.link) return `<button type="button" class="clx-link" data-open="${escapeHtml(s.link)}">${escapeHtml(s.t)}</button>`;
    return s.o ? `<span style="color:${C.orange}">${escapeHtml(s.t)}</span>` : escapeHtml(s.t);
  }).join('');
}

const CLX_CSS = `
  .clx-link{background:none;border:0;padding:0;font:inherit;color:var(--link);font-weight:500;cursor:pointer}
  .clx-link:hover{color:var(--anchor);text-decoration:underline}
  .clx-h:focus-visible,.clx-link:focus-visible,.clx-step:focus-visible{outline:2px solid var(--brand-orange);outline-offset:2px}
  .clx-step{background:none;border:0;padding:0;font:inherit;color:var(--link);cursor:pointer;user-select:none;letter-spacing:inherit;font-weight:inherit}
  .clx-step:hover{color:var(--anchor)}
  .clx-proj{display:grid;grid-template-columns:48px minmax(0,1fr) 104px 112px;gap:16px;align-items:center;padding:11px 0;border-top:1px solid var(--hair-soft);cursor:pointer}
  .clx-proj:hover{background:var(--row-hover)}
  .clx-mrow{display:flex;justify-content:space-between;align-items:baseline;gap:12px;padding:9px 0;border-top:1px solid var(--hair-soft)}
  .clx-mlabel{font-size:12.5px;color:var(--ink-2)}
  .clx-big{font-size:22px;font-weight:500;letter-spacing:-0.02em}
  .clx-name{margin:0;font-size:var(--text-h1);font-weight:var(--weight-medium);letter-spacing:-0.03em;line-height:1.05}
  .clx-past{display:grid;grid-template-columns:48px minmax(0,1fr) 104px 112px;gap:16px;align-items:center;padding:9px 0;border-top:1px solid var(--hair-soft);cursor:pointer}
  .clx-past:hover{background:var(--row-hover)}
  .clx-person{display:flex;flex-direction:column;gap:3px;padding:10px 0;border-top:1px solid var(--hair-soft);min-width:0}
  .clx-person a{color:var(--link);text-decoration:none;font-size:12.5px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
  .clx-person a:hover{text-decoration:underline}
  @media (max-width:760px){.clx-name{font-size:var(--text-h1-phone)}.clx-proj,.clx-past{grid-template-columns:44px minmax(0,1fr) auto}.clx-proj>:nth-child(4),.clx-past>:nth-child(4){display:none}}
  @media (max-width:860px){.clx-body{grid-template-columns:minmax(0,1fr)!important;gap:36px!important}.clx-aside{border-left:0!important;padding-left:0!important}.clx-dhead{flex-wrap:wrap}}
`;

export async function renderClients(mount, { query, session, onOpenProject, onOpenEnquiry, onNewEnquiry, openClientId } = {}) {
  const roles = (session && session.roles) || [];
  // Money needs the finance module as well as a money role, as on Home.
  const isFinance = hasFeature('finance') && roles.some((r) => FINANCE.includes(r));
  const canSeeEnquiries = roles.some((r) => ['Owner', 'Director', 'Finance', 'Coordinator'].includes(r));
  const canEdit = canSeeEnquiries;
  if (query) LIST_STATE.q = query;

  mount.innerHTML = `<div id="loading">${skeleton()}</div>`;
  let clients;
  try { clients = await allClients(); }
  catch (e) {
    const box = mount.querySelector('#loading');
    if (box) box.textContent = 'Couldn’t load clients: ' + (e && e.message || e);
    return;
  }
  const [projects, enquiries, invoices, users, insights, settings] = await Promise.all([
    allProjects().catch(() => []),
    canSeeEnquiries ? allEnquiries().catch(() => []) : [],
    isFinance ? allInvoices().catch(() => []) : [],
    allUsers().catch(() => []),
    clientInsights().catch(() => ({ byId: {}, computedAt: null })),
    orgSettings().catch(() => ({})),
  ]);
  // The company's payment terms (Settings › Money): a client without its own uses these (v4.26.7).
  const defaultTerms = ((settings || {}).money || {}).paymentTermsDays ?? null;

  const now = new Date();
  const current = invoicesCurrent(invoices, now);
  const moneyOn = isFinance && current.on;
  // "Money columns are off" explains invoices that STOPPED being recorded
  // (ASP, after the V1 cutover). A company that never recorded any has nothing
  // to explain, so it says nothing rather than repeat ASP's history.
  const moneyStale = isFinance && !moneyOn && !!current.last;
  const usersById = Object.fromEntries(users.map((u) => [u.id, u]));
  const index = indexByClient(clients, {
    projects: projects.filter((p) => !p.isTest),
    enquiries: enquiries.filter((e) => !e.isTest),
    invoices,
  });
  const active = clients.filter((c) => !c.archived);
  const all = active.map((c) => buildClient(c, index[c.id], {
    moneyOn, users: usersById, insight: insights.byId[c.id], now, similar: similarTo(c, clients), defaultTerms,
  }));
  const byId = new Map(all.map((c) => [c.id, c]));
  // The screen's hero, like every other register (Karan 18/10: "the same style
  // header as on the other pages"). Prospects are listed but not on the books.
  const onBooks = all.filter((c) => !c.prospective);
  const withLive = onBooks.filter((c) => c.live > 0).length;
  const prospects = all.length - onBooks.length;
  const rawById = new Map(clients.map((c) => [c.id, c]));
  const known = new Set(clients.map((c) => String(c.name || '').trim().toLowerCase()));
  const updatedAt = toDate(insights.computedAt) || now;
  const updated = updatedAt.toDateString() === now.toDateString()
    ? updatedAt.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
    : fmtDate(updatedAt);

  // Money sorts make no sense when the money columns are off.
  if (!moneyOn && ['risk', 'overdue', 'days', 'billed'].includes(LIST_STATE.sort.key)) LIST_STATE.sort = { key: 'pri', dir: 1 };

  // v4.0.0 (p21): one table for everyone — CLIENT · SECTOR · LEAD · LIVE ·
  // DAYS TO PAY · LAST CONTACT · WHY IT'S HERE. Days to pay reads "—" unless the
  // money gate is open (Finance + current invoices), exactly as before.
  const COLS = [
    { k: 'name', label: 'Client', cls: 'c-name' },
    { k: 'sector', label: 'Sector', cls: 'c-sector' },
    { k: 'lead', label: 'Lead', cls: 'c-lead' },
    { k: 'live', label: 'Live', cls: 'c-live', num: true },
    { k: moneyOn ? 'days' : null, label: 'Days to pay', cls: 'c-days', num: true },
    { k: 'contact', label: 'Last contact', cls: 'c-contact', num: true },
    { k: 'pri', label: 'Why it’s here', cls: 'c-why' },
  ];

  mount.innerHTML = `<style>${CLX_CSS}</style><div class="clx-root bm-in-table"></div>`;
  const root = mount.querySelector('.clx-root');
  let view = 'list';
  let order = [];       // ids in the list's current sort + filter, for Previous / Next
  let detailId = null;
  let selectedId = null; // the row whose side panel is open (null = closed)

  // ── Keyboard: one listener for this render, gone when the screen changes ──
  const ac = new AbortController();
  document.addEventListener('track:route', () => ac.abort(), { once: true, signal: ac.signal });
  document.addEventListener('keydown', (e) => {
    if (!root.isConnected) { ac.abort(); return; }
    if (e.metaKey || e.ctrlKey || e.altKey) return;
    // A dialog has the keys — an OPEN one: the phone's More sheet sits in the
    // DOM, hidden, on every screen, and used to swallow ↑ / ↓ on the desktop.
    if ([...document.querySelectorAll('.tf-scrim, .overlay, [aria-modal="true"]')].some((d) => d.getClientRects().length)) return;
    const t = e.target;
    const typing = t && (t.isContentEditable || /INPUT|TEXTAREA|SELECT/.test(t.tagName));
    const inSearch = t && t.classList && t.classList.contains('clx-search');
    if (view === 'list') {
      if (typing && !inSearch) return;
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        if (!order.length) return;
        LIST_STATE.focus = Math.max(0, Math.min(order.length - 1, LIST_STATE.focus + (e.key === 'ArrowDown' ? 1 : -1)));
        select(order[LIST_STATE.focus], true);
      } else if (e.key === 'Enter' && order[LIST_STATE.focus]) {
        e.preventDefault();
        openDetail(order[LIST_STATE.focus]);
      } else if (e.key === 'Escape' && selectedId) {
        e.preventDefault();
        closePanel();
      }
    } else if (!typing) {
      if (e.key === 'Escape') { e.preventDefault(); backToList(); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); step(1); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); step(-1); }
    }
  }, { signal: ac.signal });

  // ── List ──────────────────────────────────────────────────────────────────
  const segCount = (id) => all.filter(CLIENT_SEGMENTS.find((s) => s.id === id).test).length;
  const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

  function drawList() {
    view = 'list';
    detailId = null;
    headerWhere('clients', null);
    headerBack(null, null);
    if (!CLIENT_SEGMENTS.some((s) => s.id === LIST_STATE.seg)) LIST_STATE.seg = 'all';
    const sub = [withLive ? `${withLive} with live work` : 'no live work', prospects ? plural(prospects, 'prospect') : ''].filter(Boolean).join(' · ');
    root.innerHTML = `
      <div class="clx4">
        <div class="clx4-list">
          <div class="clx4-head">
            <div class="page-h"><h1 class="ct4-page-title">Clients</h1><span class="page-sub">${escapeHtml(sub)}</span></div>
            <div class="clx4-actions">
              ${canSeeEnquiries && onNewEnquiry ? '<button type="button" class="btn primary" data-addenq>Add enquiry</button>' : ''}
            </div>
          </div>
          <div class="clx4-filters">
            <div class="seg" role="group" aria-label="Show">
              ${CLIENT_SEGMENTS.map((s) => `<button type="button" data-seg="${s.id}" class="${LIST_STATE.seg === s.id ? 'active' : ''}" aria-pressed="${LIST_STATE.seg === s.id}">${escapeHtml(s.label)}<span class="n">${segCount(s.id)}</span></button>`).join('')}
            </div>
            <label class="clx4-search">${SEARCH_ICON}<input class="clx-search" type="search" placeholder="Client, contact or lead" autocomplete="off" value="${escapeHtml(LIST_STATE.q)}" aria-label="Search clients"></label>
          </div>
          ${moneyStale ? `<p class="clx4-note">Money columns are off. Track has no invoices after ${escapeHtml(`${current.last.number}, ${fmtDate(current.last.date)}`)}, so days to pay and credit risk would be wrong. They come back on their own once invoices are recorded in Track again.</p>` : ''}
          <div class="clx4-table reg-wrap">
            <div class="clx4-row clx4-th reg-head" role="row"></div>
            <div data-rows role="rowgroup"></div>
          </div>
          <div class="clx4-foot"><span>↑↓ to move · Enter to open${moneyOn ? ' · days to pay is the average of the last six paid invoices' : ''}</span><button type="button" class="linkbtn" data-reset hidden>Back to Track priority</button></div>
          <div data-notyet></div>
        </div>
        <aside class="clx4-panel" data-panel hidden aria-label="Client"></aside>
      </div>`;
    const search = root.querySelector('.clx-search');
    search.addEventListener('input', () => { LIST_STATE.q = search.value; LIST_STATE.focus = 0; drawRows(); });
    root.querySelectorAll('[data-seg]').forEach((b) => b.addEventListener('click', () => {
      LIST_STATE.seg = b.dataset.seg; LIST_STATE.focus = 0;
      root.querySelectorAll('[data-seg]').forEach((x) => { const on = x === b; x.classList.toggle('active', on); x.setAttribute('aria-pressed', String(on)); });
      drawRows();
    }));
    root.querySelector('[data-reset]').addEventListener('click', () => { LIST_STATE.sort = { key: 'pri', dir: 1 }; LIST_STATE.focus = 0; drawRows(); });
    root.querySelector('[data-addenq]')?.addEventListener('click', () => onNewEnquiry(null));
    drawRows();
    if (selectedId && byId.has(selectedId)) select(selectedId, true);
  }

  function drawHead() {
    const { key, dir } = LIST_STATE.sort;
    const head = root.querySelector('.clx4-th');
    head.innerHTML = COLS.map((c) => {
      if (!c.k) return `<span class="${c.cls}${c.num ? ' num' : ''}">${escapeHtml(c.label)}</span>`;
      const on = key === c.k && !(c.k === 'pri' && dir > 0);
      return `<button type="button" class="clx-h ${c.cls}${c.num ? ' num' : ''}" data-k="${c.k}" aria-sort="${key === c.k ? (dir > 0 ? 'descending' : 'ascending') : 'none'}">${escapeHtml(c.label)}${on ? `<span aria-hidden="true">${dir > 0 ? '↓' : '↑'}</span>` : ''}</button>`;
    }).join('');
    head.querySelectorAll('[data-k]').forEach((b) => b.addEventListener('click', () => {
      const k = b.dataset.k;
      const s = LIST_STATE.sort;
      LIST_STATE.sort = s.key === k ? { key: k, dir: -s.dir } : { key: k, dir: 1 };
      LIST_STATE.focus = 0;
      drawRows();
    }));
    const reset = root.querySelector('[data-reset]');
    reset.hidden = key === 'pri' && dir > 0;
    reset.textContent = `Sorted by ${sortLabel(LIST_STATE.sort).toLowerCase()} · back to Track priority`;
  }

  function cellsFor(c) {
    const over = c.daysToPay != null && c.daysToPay > c.terms;
    const why = whyHere(c, { moneyOn, now });
    return [
      `<span class="ph-name c-name" title="${escapeHtml(c.name)}">${escapeHtml(c.name)}</span>`,
      `<span class="c-sector">${escapeHtml(schemeLabel(c.sector) || '—')}</span>`,
      `<span class="ph-sub c-lead">${escapeHtml(c.lead || '—')}</span>`,
      `<span class="c-live num">${c.live || 0}</span>`,
      `<span class="c-days num${over ? ' late-text' : ''}" title="${escapeHtml(`Days to pay / terms: ${c.terms === 0 ? 'on receipt' : `${c.terms} days`}`)}">${c.daysToPay == null ? '<span class="mute">—</span>' : `${c.daysToPay}/${c.terms}`}</span>`,
      `<span class="ph-state c-contact num${c.last != null && c.last > 30 ? ' late-text strong' : ''}">${escapeHtml(contactLabel(c.last).replace(/^Today$/, 'today'))}</span>`,
      `<span class="c-why${why.attn ? ' late-text' : ''}" title="${escapeHtml(why.text)}">${escapeHtml(why.text)}</span>`,
    ].join('');
  }

  function visible() {
    const seg = CLIENT_SEGMENTS.find((s) => s.id === LIST_STATE.seg) || CLIENT_SEGMENTS[0];
    return sortClients(all.filter((c) => seg.test(c) && matches(c, LIST_STATE.q)), LIST_STATE.sort);
  }

  function drawRows() {
    drawHead();
    const q = LIST_STATE.q.trim();
    const list = visible();
    order = list.map((c) => c.id);
    LIST_STATE.focus = Math.max(0, Math.min(LIST_STATE.focus, list.length - 1));
    const rowsEl = root.querySelector('[data-rows]');
    rowsEl.innerHTML = list.length
      ? list.map((c) => `<div class="clx4-row reg-row" data-reg="clients" role="row" tabindex="-1" data-id="${escapeHtml(c.id)}">${cellsFor(c)}</div>`).join('')
      : `<div class="clx4-none">${q ? `No clients match “${escapeHtml(q)}”. <button type="button" class="linkbtn" data-clear>Clear search</button>` : 'No clients here.'}</div>`;
    rowsEl.querySelectorAll('.clx4-row').forEach((el, i) => {
      el.addEventListener('click', () => { LIST_STATE.focus = i; select(el.dataset.id, false); });
      el.addEventListener('dblclick', () => openDetail(el.dataset.id));
    });
    const clear = rowsEl.querySelector('[data-clear]');
    if (clear) clear.addEventListener('click', () => { LIST_STATE.q = ''; root.querySelector('.clx-search').value = ''; drawRows(); });
    drawNotYet(q);
    paintFocus(false);
  }

  // A client record is only made when an enquiry is WON or settled as new, so
  // a name can still sit only on its enquiry (Karan 16/10, "newtown"). Search
  // shows those too, each opening its enquiry.
  function drawNotYet(q) {
    const box = root.querySelector('[data-notyet]');
    const n = q.toLowerCase();
    const hits = !n || !canSeeEnquiries ? [] : enquiries.filter((e) => !e.isTest && !e.clientRef && !['Won', 'Lost', 'Dismissed', 'Spam'].includes(e.status)
      && [e.clientName, e.clientCompany, e.clientContact, e.clientEmail].some((v) => String(v || '').toLowerCase().includes(n))
      && !known.has(String(e.clientName || '').trim().toLowerCase())).slice(0, 8);
    box.innerHTML = hits.length ? `<div class="clx4-notyet">
        <div class="clx4-label">Not clients yet · on an open enquiry</div>
        ${hits.map((e) => `<button type="button" data-enq="${escapeHtml(e.id)}" class="clx4-notyet-row">
          <span>${escapeHtml(e.clientName || e.clientCompany || 'Unnamed')}</span>
          <span class="num-tech link">${escapeHtml(e.number || 'NEW')}</span>
          <span class="mute">${escapeHtml(sentence(e.status || ''))} · becomes a client when it’s won</span></button>`).join('')}</div>` : '';
    box.querySelectorAll('[data-enq]').forEach((b) => b.addEventListener('click', () => onOpenEnquiry && onOpenEnquiry(b.dataset.enq)));
  }

  function paintFocus(scroll) {
    const rows = root.querySelectorAll('.clx4-row[data-id]');
    rows.forEach((el) => el.classList.toggle('on', el.dataset.id === selectedId));
    const el = selectedId ? root.querySelector(`.clx4-row[data-id="${CSS.escape(selectedId)}"]`) : null;
    if (el && scroll) el.scrollIntoView({ block: 'nearest' });
  }

  // ── The docked side panel (p21) ───────────────────────────────────────────
  function closePanel() {
    selectedId = null;
    const panel = root.querySelector('[data-panel]');
    if (panel) { panel.hidden = true; panel.innerHTML = ''; }
    root.querySelector('.clx4')?.classList.remove('has-panel');
    paintFocus(false);
  }

  function select(id, scroll) {
    if (!byId.has(id)) return;
    selectedId = id;
    const i = order.indexOf(id);
    if (i >= 0) LIST_STATE.focus = i;
    paintFocus(scroll);
    drawPanel(byId.get(id));
  }

  function drawPanel(c) {
    const panel = root.querySelector('[data-panel]');
    if (!panel) return;
    const raw = rawById.get(c.id) || {};
    const why = whyHere(c, { moneyOn, now });
    const { brief } = clientBrief(c, { moneyOn });
    const email = (Array.isArray(raw.emails) ? raw.emails : [raw.emails]).map((e) => String(e || '').trim()).find(Boolean) || '';
    const contact = [raw.contactName, email].filter(Boolean);
    const live = c.liveProjects.slice().sort((a, b) => String(b.number || '').localeCompare(String(a.number || ''), 'en', { numeric: true }));
    const openEnq = c.enquiries.filter((e) => ['Enquiry', 'Quoted'].includes(e.status));
    const eyebrow = [schemeLabel(c.sector), c.prospective ? 'prospect' : '', c.leadFull ? `lead ${c.leadFull}` : ''].filter(Boolean).join(' · ');
    const kv = (k, v) => `<div class="clx4-kv"><span class="k">${escapeHtml(k)}</span><span class="v">${v}</span></div>`;
    panel.innerHTML = `
      <div class="clx4-ptop">
        <div class="clx4-pid">
          ${eyebrow ? `<div class="clx4-eyebrow">${escapeHtml(eyebrow)}</div>` : ''}
          <h2 class="clx4-pname"><button type="button" data-full title="Open the client page">${escapeHtml(c.name)}</button></h2>
        </div>
        <button type="button" class="btn icon clx4-x" data-close aria-label="Close">×</button>
      </div>
      <div class="clx4-pbtns">
        <button type="button" class="btn" data-full>Open client</button>
        ${email ? '<button type="button" class="btn" data-email>Email</button>' : ''}
        ${canSeeEnquiries && onNewEnquiry ? '<button type="button" class="btn" data-newenq>New enquiry</button>' : ''}
      </div>
      ${why.lead ? `<div class="clx4-alert${why.attn ? '' : ' calm'}"><span class="dot" aria-hidden="true"></span><p><b>${escapeHtml(why.lead)}</b> ${segHtml(brief, canSeeEnquiries)}</p></div>` : ''}
      <div class="clx4-kvs">
        ${kv('Main contact', contact.length ? contact.map(escapeHtml).join(' · ') : '<span class="mute">None on file</span>')}
        ${kv('Live projects', live.length ? live.map((p) => `<button type="button" class="clx4-plink" data-project="${escapeHtml(p.id)}">${escapeHtml([p.number, p.title || p.service].filter(Boolean).join(' '))}</button>`).join('') : '<span class="mute">None</span>')}
        ${canSeeEnquiries ? kv('Enquiries', openEnq.length === 1
          ? `<button type="button" class="clx4-plink" data-enq="${escapeHtml(openEnq[0].id)}">${escapeHtml(openEnq[0].number || '1 open')}${openEnq[0].number ? ' · open' : ''}</button>`
          : openEnq.length ? `${openEnq.length} open` : 'None open') : ''}
        ${isFinance ? kv('Billed this FY', moneyOn ? amount(c.billed, true) : secretFig('', false)) : ''}
        ${moneyOn ? kv('Credit risk', escapeHtml(c.risk || 'Low')) : ''}
      </div>`;
    panel.hidden = false;
    root.querySelector('.clx4')?.classList.add('has-panel');
    panel.querySelector('[data-close]').addEventListener('click', closePanel);
    panel.querySelectorAll('[data-full]').forEach((b) => b.addEventListener('click', () => openDetail(c.id)));
    panel.querySelector('[data-newenq]')?.addEventListener('click', () => onNewEnquiry(c.id));
    panel.querySelector('[data-email]')?.addEventListener('click', async () => {
      try { const { renderCompose } = await import('./mail-compose.js'); renderCompose(session, { to: email }); }
      catch { location.href = `mailto:${encodeURIComponent(email)}`; }
    });
    panel.querySelectorAll('[data-project]').forEach((b) => b.addEventListener('click', () => onOpenProject && onOpenProject(b.dataset.project)));
    panel.querySelectorAll('[data-enq]').forEach((b) => b.addEventListener('click', () => onOpenEnquiry && onOpenEnquiry(b.dataset.enq)));
    panel.querySelectorAll('.clx-link[data-open]').forEach((b) => b.addEventListener('click', () => openDetail(b.dataset.open)));
  }
  // ── Detail ────────────────────────────────────────────────────────────────
  function openDetail(id, { push = true } = {}) {
    if (!byId.has(id)) return;
    if (!order.includes(id)) order = sortClients(all.filter((c) => matches(c, LIST_STATE.q)), LIST_STATE.sort).map((c) => c.id);
    if (push && location.pathname !== `/clients/${encodeURIComponent(id)}`) history.pushState({ view: 'clients', detailId: id }, '', `/clients/${encodeURIComponent(id)}`);
    drawDetail(id);
    window.scrollTo(0, 0);
    document.querySelector('main')?.scrollTo?.(0, 0);
  }
  function backToList() {
    const i = order.indexOf(detailId);
    if (i >= 0) LIST_STATE.focus = i;
    if (location.pathname !== '/clients') history.pushState({ view: 'clients', detailId: null }, '', '/clients');
    drawList();
    paintFocus(true);
  }
  function step(dir) {
    if (!order.length) return;
    const i = order.indexOf(detailId);
    openDetail(order[(i + dir + order.length) % order.length]);
  }

  function drawDetail(id) {
    view = 'detail';
    detailId = id;
    const c = byId.get(id);
    const raw = rawById.get(id) || {};
    headerWhere('clients', c.name);
    headerBack('Clients', backToList);
    const pos = order.indexOf(id);
    const { brief, growth } = clientBrief(c, { moneyOn });
    const acts = suggestedActions(c, { moneyOn });
    const ec = flagColour(c.flag);
    const leadName = c.leadFull || '—';
    const m = c.money;
    const rs = RISK_STYLE[c.risk] || RISK_STYLE.Low;
    const contacts = contactsFor(raw, c);
    const pastSorted = c.projects.filter((p) => !isLive(p.status)).sort((a, b) => String(b.number || '').localeCompare(String(a.number || ''), 'en', { numeric: true }));
    const projectsSorted = c.liveProjects.slice().sort((a, b) => stageIndex(b.status) - stageIndex(a.status) || String(b.number || '').localeCompare(String(a.number || '')));

    root.innerHTML = `
      <div>
        <nav class="crumbs clx4-crumbs" aria-label="Breadcrumb"><button type="button" data-back>Clients</button><span>/</span><span>${escapeHtml(c.name)}</span>
          <span class="clx4-steps"><button type="button" class="clx-step" data-step="-1">‹ Previous</button><span>${pos >= 0 ? `${pos + 1} of ${order.length}` : ''}</span><button type="button" class="clx-step" data-step="1">Next ›</button></span></nav>
                <div class="clx-dhead" style="margin-top:6px;display:flex;align-items:flex-end;gap:22px;padding-bottom:28px;border-bottom:1px solid var(--hair)">
          <div style="min-width:0">
            <h1 class="clx-name ct4-page-title">${escapeHtml(c.name)}</h1>
            <div style="margin-top:12px;display:flex;align-items:center;gap:14px;font-size:13.5px;color:${C.ink2};flex-wrap:wrap">
              ${c.flag ? `<span style="display:flex;align-items:center;gap:7px;color:${ec}"><span style="width:6px;height:6px;border-radius:50%;background:${ec}"></span>${escapeHtml(c.flag)}</span><span style="color:${C.grey}">·</span>` : ''}
              ${c.prospective ? `<span style="color:${C.blue}">Prospect</span><span style="color:${C.grey}">·</span>` : ''}
              ${c.sector ? `<span>${escapeHtml(schemeLabel(c.sector))}</span><span style="color:${C.grey}">·</span>` : ''}
              ${c.since ? `<span>Client since ${c.since}</span><span style="color:${C.grey}">·</span>` : ''}
              <span>Lead ${escapeHtml(leadName)}</span>
            </div>
          </div>
          <span style="flex:1"></span>
          <div style="display:flex;align-items:center;gap:12px">
            ${canEdit ? '<button type="button" class="clx-link" data-archive style="font-weight:400;font-size:12.5px;color:var(--ink-3)">Archive</button>' : ''}
            ${isFinance ? '<button type="button" class="btn" data-statement title="Every open invoice, how old the balance is, and how to pay">Statement</button>' : ''}
            <button type="button" class="btn" data-edit>${canEdit ? 'Edit client' : 'Client record'}</button>
            ${canSeeEnquiries && onNewEnquiry ? '<button type="button" class="btn primary" data-newenq>New enquiry</button>' : ''}
          </div>
        </div>
        <div class="clx-body" style="display:grid;grid-template-columns:minmax(0,1fr) 320px;gap:56px;padding:32px 0 56px">
          <div style="display:flex;flex-direction:column;gap:36px;min-width:0">
            <div style="display:flex;flex-direction:column;gap:14px">
              <span style="${EYEBROW};color:${C.orange}">Track’s briefing · updated ${escapeHtml(updated)}${updatedAt.toDateString() === now.toDateString() ? ' today' : ''}</span>
              <p style="margin:0;font-size:24px;line-height:1.4;letter-spacing:-0.012em;text-wrap:pretty;max-width:860px">${segHtml(brief, canSeeEnquiries)}</p>
              ${growth.length ? `<p style="margin:0;font-size:15px;line-height:1.6;color:${C.ink2};text-wrap:pretty;max-width:680px">${segHtml(growth, canSeeEnquiries)}</p>` : ''}
            </div>
            <div style="display:flex;flex-direction:column">
              <div style="display:flex;align-items:baseline;gap:12px;padding-bottom:10px"><span style="${EYEBROW};color:${C.blue}">Suggested this week</span><span data-actsnote style="font-size:12px;color:${C.muted}"></span></div>
              <div data-acts></div>
              <div style="border-top:1px solid var(--hair)"></div>
            </div>
            <div style="display:flex;flex-direction:column">
              <div style="display:flex;align-items:baseline;gap:12px;padding-bottom:10px;min-width:0"><span style="${EYEBROW};color:${C.blue};white-space:nowrap">Live projects · ${projectsSorted.length}</span><span style="font-size:12px;color:${C.muted};white-space:nowrap;overflow:hidden;text-overflow:ellipsis;min-width:0">${STAGES.join(' · ')}</span></div>
              ${projectsSorted.length ? projectsSorted.map((p) => projectRow(p)).join('') : `<div style="padding:14px 0;border-top:1px solid var(--hair-soft);font-size:14px;color:${C.ink2}">No live projects.</div>`}
              <div style="border-top:1px solid var(--hair-soft)"></div>
            </div>
            ${pastSorted.length ? `<div style="display:flex;flex-direction:column">
              <div style="display:flex;align-items:baseline;gap:12px;padding-bottom:10px"><span style="${EYEBROW};color:${C.muted}">Earlier projects · ${pastSorted.length}</span><span style="font-size:12px;color:${C.muted}">Newest first</span></div>
              ${pastSorted.map((p) => pastRow(p)).join('')}
              <div style="border-top:1px solid var(--hair-soft)"></div>
            </div>` : ''}
          </div>
          <aside class="clx-aside" style="display:flex;flex-direction:column;gap:28px;border-left:1px solid var(--hair);padding-left:28px;min-width:0">
            ${moneyOn ? `<div style="display:flex;flex-direction:column">
              <span style="${EYEBROW};color:${C.muted};padding-bottom:8px">Money</span>
              <div class="clx-mrow"><span class="clx-mlabel">Overdue</span><span class="clx-big" style="color:${c.overdue ? C.orange : C.ink}">${amount(c.overdue, true)}</span></div>
              <div class="clx-mrow"><span class="clx-mlabel">Days to pay</span><span class="clx-big" style="color:${c.daysToPay != null && c.daysToPay > c.terms ? C.orange : C.ink}">${c.daysToPay == null ? '—' : c.daysToPay}<span style="font-size:13px;font-weight:400;color:${C.muted}"> / ${c.terms === 0 ? 'on receipt' : `${c.terms} terms`}</span></span></div>
              <div class="clx-mrow"><span class="clx-mlabel">Credit risk</span><span style="display:flex;align-items:center;gap:7px;font-size:15px;font-weight:500;color:${rs.ink === C.muted ? C.ink : rs.ink}"><span style="width:7px;height:7px;border-radius:50%;background:${rs.dot}"></span>${escapeHtml(c.risk)}</span></div>
              <div class="clx-mrow"><span class="clx-mlabel">Billed this year</span><span style="${MONO};font-size:14px">${amount(c.billed, true)}</span></div>
              <div style="display:flex;flex-direction:column;padding-top:6px">
                ${(m?.lines || []).map((l) => `<div style="display:grid;grid-template-columns:40px minmax(0,1fr) auto;gap:10px;align-items:baseline;padding:6px 0;font-size:12px">
                  <span style="${MONO};color:${C.dark}">${escapeHtml(l.number)}</span>
                  <span style="color:${l.late ? C.orange : C.ink2}">${escapeHtml(l.state)}</span>
                  <span style="${MONO};color:${C.ink2}">${amount(l.amount, true)}</span></div>`).join('')}
              </div>
            </div>` : moneyStale ? `<div style="display:flex;flex-direction:column">
              <span style="${EYEBROW};color:${C.muted};padding-bottom:8px">Money</span>
              <p style="margin:0;padding-top:9px;border-top:1px solid var(--hair-soft);font-size:12.5px;line-height:1.5;color:${C.ink2}">Off until invoices are recorded in Track again. The last is ${escapeHtml(`${current.last.number}, ${fmtDate(current.last.date)}`)}.</p>
            </div>` : ''}
            <div style="display:flex;flex-direction:column">
              <span style="${EYEBROW};color:${C.muted};padding-bottom:8px">Client Experience Panel</span>
              <div class="clx-mrow"><span class="clx-mlabel">${c.rating == null ? 'No ratings yet' : `Average, ${c.ratingCount} rating${c.ratingCount === 1 ? '' : 's'}`}</span><span class="clx-big" style="color:${c.rating != null && c.rating < 4 ? C.orange : C.ink}">${c.rating == null ? '—' : c.rating.toFixed(1)}</span></div>
            </div>
            ${canSeeEnquiries ? `<div style="display:flex;flex-direction:column">
              <span style="${EYEBROW};color:${C.muted};padding-bottom:8px">Growth</span>
              <div class="clx-mrow"><span class="clx-mlabel">Open enquiries</span><span style="${MONO};font-size:14px;color:${c.openEnquiries ? C.blue : C.muted}">${c.openEnquiries ? `${c.openEnquiries}${c.openValue ? ` · ${amount(c.openValue, canSeeEnquiries)}` : ''}` : 'None'}</span></div>
              <div class="clx-mrow"><span class="clx-mlabel">Projects${c.since ? ` since ${c.since}` : ''}</span><span style="${MONO};font-size:14px">${c.projects.length}</span></div>
              <div class="clx-mrow"><span class="clx-mlabel">Quotes won</span><span style="${MONO};font-size:14px">${c.decided ? `${c.won} of ${c.decided}` : '—'}</span></div>
            </div>` : ''}
            <div style="display:flex;flex-direction:column">
              <span style="${EYEBROW};color:${C.muted};padding-bottom:8px">Contacts</span>
              ${contacts.map(personHtml).join('')}
              <div data-mailcontacts></div>
              ${raw.address ? `<div class="clx-person"><span style="font-size:12px;color:${C.muted}">Address</span><span style="font-size:13px;line-height:1.45">${escapeHtml(raw.address)}</span></div>` : ''}
              ${!contacts.length && !raw.address ? `<div data-nocontacts style="padding:9px 0;border-top:1px solid var(--hair-soft);font-size:12.5px;color:${C.muted}">None on file.</div>` : ''}
            </div>
          </aside>
        </div>
      </div>`;

    root.querySelector('[data-back]').addEventListener('click', backToList);
    root.querySelectorAll('[data-step]').forEach((b) => b.addEventListener('click', () => step(Number(b.dataset.step))));
    root.querySelectorAll('[data-project]').forEach((el) => el.addEventListener('click', () => onOpenProject && onOpenProject(el.dataset.project)));
    root.querySelector('[data-edit]').addEventListener('click', () => openRecordCard(c));
    // v4.19.1: the client's statement of account (views/fin-overdue.js → doc-print.js), Finance only.
    root.querySelector('[data-statement]')?.addEventListener('click', () => import('./fin-overdue.js').then((m) => m.openStatement(c.id, { session, clients, invoices: isFinance ? invoices : [], projects })));
    root.querySelector('[data-newenq]')?.addEventListener('click', () => onNewEnquiry(c.id));
    root.querySelector('[data-archive]')?.addEventListener('click', () => openArchiveConfirm(c));
    drawActions(c, acts);
    fillProjectDetail(c);
    fillMailContacts(c, contacts);
    root.querySelectorAll('.clx-past[data-project]').forEach((el) => el.addEventListener('keydown', (e) => { if (e.key === 'Enter') onOpenProject && onOpenProject(el.dataset.project); }));
  }

  function projectRow(p) {
    const k = stageIndex(p.status);
    const dots = STAGES.map((_, i) => {
      const size = i === k ? 10 : 7;
      return `<span style="width:${size}px;height:${size}px;border-radius:50%;flex:none;background:${i <= k ? C.blue : 'var(--surface)'};box-shadow:${i <= k ? 'none' : 'inset 0 0 0 1px var(--hair-strong)'}"></span>`;
    }).join('');
    return `<div class="clx-proj" role="link" tabindex="0" data-project="${escapeHtml(p.id)}">
      <span style="${MONO};font-size:13px;color:${C.dark}">${escapeHtml(p.number || '—')}</span>
      <span style="display:flex;flex-direction:column;gap:3px;min-width:0"><span style="font-size:14px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis">${escapeHtml(p.title || p.service || '—')}</span><span data-next="${escapeHtml(p.id)}" style="font-size:12px;color:${C.muted};white-space:nowrap;overflow:hidden;text-overflow:ellipsis">${escapeHtml(p.service && p.title ? p.service : '')}</span></span>
      <span style="display:flex;flex-direction:column;gap:5px"><span style="display:flex;align-items:center;gap:6px;height:10px">${dots}</span><span style="font-size:12px;white-space:nowrap">${escapeHtml(STAGES[k] || p.status || '')}</span></span>
      <span data-fee="${escapeHtml(p.id)}" style="${MONO};font-size:12px;text-align:right;color:${C.ink2};min-width:0;overflow:hidden;white-space:nowrap"></span>
    </div>`;
  }

  function pastRow(p) {
    const d = toDate(p.createdAt);
    return `<div class="clx-past" role="link" tabindex="0" data-project="${escapeHtml(p.id)}">
      <span style="${MONO};font-size:13px;color:${C.dark}">${escapeHtml(p.number || '—')}</span>
      <span style="font-size:13.5px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;min-width:0">${escapeHtml(p.title || p.service || '—')}</span>
      <span style="font-size:12px;color:${C.ink2};white-space:nowrap">${escapeHtml(p.status || '—')}</span>
      <span style="font-size:12px;color:${C.muted};text-align:right">${d ? d.getFullYear() : ''}</span>
    </div>`;
  }

  // The next step and the fee need the project's instructions — one
  // collection-group read, made once and only when a client is opened.
  let instrP = null;
  async function fillProjectDetail(c) {
    if (!c.liveProjects.length) return;
    instrP ||= allInstructionsIndex(2000).catch(() => []);
    const instr = await instrP;
    if (detailId !== c.id) return;
    for (const p of c.liveProjects) {
      const mine = instr.filter((i) => i.projectId === p.id).sort((a, b) => (a.seq || 0) - (b.seq || 0));
      const open = mine.find((i) => !['Complete', 'Issued', 'Void'].includes(i.status)) || mine[mine.length - 1];
      const nextEl = root.querySelector(`[data-next="${CSS.escape(p.id)}"]`);
      if (nextEl && open) {
        const due = toDate(open.due);
        const late = due && due < now && stageIndex(p.status) < 4;
        const words = [open.description, due ? `due ${due.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}` : ''].filter(Boolean).join(' · ');
        if (words) { nextEl.textContent = words + (late ? ' · overdue' : ''); if (late) nextEl.style.color = C.orangeInk; }
      }
      const feeEl = root.querySelector(`[data-fee="${CSS.escape(p.id)}"]`);
      if (feeEl && moneyOn) {
        const total = mine.reduce((s, i) => s + (Number(i.fee) || 0), 0);
        let paid = (index[c.id]?.invoices || []).filter((iv) => iv.paymentStatus === 'Paid' && refTail(iv.projectRef) === p.id).reduce((s, iv) => s + (Number(iv.amount) || 0), 0);
        // A project brought in from before Track (go-live D) carries an opening
        // balance apart from Track's invoices: added here ONCE, and labelled.
        let before = 0;
        if (p.broughtIn) { try { const { openingBalance } = await import('../data/bring-in.js'); const o = await openingBalance(p.id); before = Number((o || {}).paid) || 0; paid += before; } catch (_) { /* not readable by this role */ } }
        if (total) feeEl.innerHTML = `${amount(paid, true)} / ${amount(total, true)}${before ? ` <span class="muted" title="Paid before Track, brought in as an opening balance">· ${amount(before, true)} before Track</span>` : ''}`;
      }
    }
  }

  // Suggested this week — each button raises a task for the client's lead.
  async function drawActions(c, acts) {
    const box = root.querySelector('[data-acts]');
    const note = root.querySelector('[data-actsnote]');
    const leadUser = c.leadUser && c.leadUser.email ? { id: c.leadId, ...c.leadUser } : null;
    const meUser = users.find((u) => String(u.email || '').toLowerCase() === String(session?.email || '').toLowerCase());
    const assignee = leadUser || (meUser ? { ...meUser } : null);
    const forName = leadUser ? (leadUser.displayName || 'the lead') : 'you';
    let raised = {};
    const paint = () => {
      if (detailId !== c.id) return;
      if (!acts.length) {
        box.innerHTML = `<div style="padding:14px 0;border-top:1px solid var(--hair);font-size:14.5px;color:${C.ink2}">Nothing this week. Track will flag this client if payment, ratings or enquiries change.</div>`;
        note.textContent = '';
        return;
      }
      const openN = acts.filter((a) => !raised[a.key]).length;
      note.textContent = openN ? `${openN} open` : 'All raised as tasks';
      box.innerHTML = acts.map((a, i) => {
        const done = raised[a.key];
        return `<div style="display:grid;grid-template-columns:36px minmax(0,1fr) auto;gap:16px;align-items:center;padding:12px 0;border-top:1px solid var(--hair);min-height:40px">
          <span style="${MONO};font-size:13px;color:${done ? C.dark : C.muted}">${String(i + 1).padStart(2, '0')}</span>
          <span style="font-size:14.5px;line-height:1.5;color:${done ? C.muted : C.ink}">${escapeHtml(a.text)}</span>
          ${done
            ? `<span style="display:flex;align-items:center;gap:8px;font-size:12.5px;color:${C.dark};white-space:nowrap"><span style="width:6px;height:6px;border-radius:50%;background:${C.dark}"></span>Task raised for ${escapeHtml(done.forName || forName)}<button type="button" class="clx-link" data-undo="${escapeHtml(a.key)}" style="color:var(--ink-3);font-weight:400;padding-left:6px">Undo</button></span>`
            : `<button type="button" class="tf-btn ${i === 0 ? 'tf-btn-primary' : 'tf-btn-secondary'}" data-act="${escapeHtml(a.key)}" style="white-space:nowrap">${escapeHtml(a.button)}</button>`}
        </div>`;
      }).join('');
      box.querySelectorAll('[data-act]').forEach((b) => b.addEventListener('click', async () => {
        const a = acts.find((x) => x.key === b.dataset.act);
        b.disabled = true; b.textContent = 'Raising…';
        try {
          const t = await createTask({
            title: a.text,
            description: `Suggested by Track on ${c.name}'s client page.`,
            projectId: a.projectId || null,
            assigneeId: assignee ? assignee.id : null,
            assigneeEmail: assignee ? assignee.email : null,
            source: 'system', sourceLabel: 'CLIENTS', sourceRef: `clients/${c.id}#${a.key}`,
          }, session);
          raised[a.key] = { id: t.id, forName };
          paint();
        } catch (e) {
          b.disabled = false; b.textContent = a.button;
          note.textContent = `Couldn’t raise it: ${e.message || e}`;
        }
      }));
      box.querySelectorAll('[data-undo]').forEach((b) => b.addEventListener('click', async () => {
        const r = raised[b.dataset.undo];
        if (!r) return;
        b.disabled = true;
        try {
          // Tasks are never deleted (rules); a withdrawn one is closed with its trail.
          await updateTask(r.id, { withdrawn: true }, session);
          await setTaskState(r.id, 'done', session);
          delete raised[b.dataset.undo];
          paint();
        } catch (e) { b.disabled = false; note.textContent = `Couldn’t undo: ${e.message || e}`; }
      }));
    };
    paint();
    // Already raised this fortnight? Show it as raised, not as a fresh suggestion.
    try {
      const tasks = await clientActionTasks(c.id);
      const fresh = (t) => !t.withdrawn && (Date.now() - (toDate(t.createdAt) || 0)) < 14 * 86400000;
      for (const t of tasks.filter(fresh)) {
        const key = String(t.sourceRef || '').split('#')[1];
        if (key) raised[key] = { id: t.id, forName: (usersById[t.assigneeId] && usersById[t.assigneeId].displayName) || forName };
      }
      paint();
    } catch (e) { /* the suggestions still work; they just can't show what was raised before */ }
  }

  // On the record first: the named contact, every email on file, the extra
  // contacts line, then whoever wrote in on an enquiry. { n, role, email, phone }.
  function contactsFor(raw, c) {
    const out = [];
    // One person once: the same name or the same address is the same contact,
    // and a later sighting only fills in what the first one lacked.
    const add = (k) => {
      const keys = [k.email, k.n].map((v) => String(v || '').trim().toLowerCase()).filter(Boolean);
      if (!keys.length) return;
      const prev = out.find((o) => [o.email, o.n].some((v) => keys.includes(String(v || '').trim().toLowerCase())));
      if (prev) { prev.email ||= k.email; prev.phone ||= k.phone; return; }
      out.push(k);
    };
    const emails = (Array.isArray(raw.emails) ? raw.emails : []).map((e) => String(e || '').trim()).filter(Boolean);
    const phone = raw.contactNumber && raw.contactNumber !== '0' ? String(raw.contactNumber) : '';
    if (raw.contactName || phone) add({ n: raw.contactName || emails[0] || 'Main contact', role: 'Main contact', email: emails[0] || '', phone });
    emails.forEach((e) => add({ n: e, role: 'Email on file', email: e }));
    String(raw.v2Contacts || '').split(/\n|;/).map((x) => x.trim()).filter(Boolean).forEach((x) => {
      const ph = (x.match(/\+?\d[\d\s]{8,}\d/) || [''])[0].trim();
      const em = (x.match(/[^\s<>,;]+@[^\s<>,;]+\.[a-z]{2,}/i) || [''])[0];
      add({ n: x.replace(ph, '').replace(em, '').replace(/[\s—–:,-]+$/, '').trim() || x, role: 'Other contact', email: em, phone: ph });
    });
    c.enquiries.slice().sort((a, b) => (toDate(b.receivedAt) || 0) - (toDate(a.receivedAt) || 0))
      .forEach((e) => { if (e.clientContact || e.clientEmail) add({ n: e.clientContact || e.clientEmail, role: e.number ? `Enquired on ${e.number}` : 'Enquiry', email: e.clientEmail || '', phone: e.clientPhone || '' }); });
    return out;
  }

  function personHtml(k) {
    const tel = String(k.phone || '').replace(/[^\d+]/g, '');
    return `<div class="clx-person">
      <span style="font-size:13.5px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${escapeHtml(k.n)}</span>
      ${k.role ? `<span style="font-size:12px;color:${C.muted}">${escapeHtml(k.role)}</span>` : ''}
      ${k.email && k.email !== k.n ? `<a href="mailto:${escapeHtml(k.email)}">${escapeHtml(k.email)}</a>` : k.email ? `<a href="mailto:${escapeHtml(k.email)}">Email</a>` : ''}
      ${tel ? `<a href="tel:${escapeHtml(tel)}">${escapeHtml(k.phone)}</a>` : ''}
    </div>`;
  }

  // Who we actually email about this client's work, from the mail index —
  // most records carry no contact at all (Alstom Derby, 18/10).
  async function fillMailContacts(c, onFile) {
    const nos = [...c.projects.map((p) => p.number), ...c.enquiries.map((e) => e.number)].filter(Boolean);
    if (!nos.length) return;
    let rows;
    try { rows = await emailsForNumbers(nos); } catch { return; }
    if (detailId !== c.id) return;
    const box = root.querySelector('[data-mailcontacts]');
    if (!box) return;
    const ownDomain = String(session?.email || '').split('@')[1] || '';
    const known = new Set(onFile.map((k) => String(k.email || '').toLowerCase()).filter(Boolean));
    const found = contactsFromMail(rows, { ownDomain }).filter((k) => !known.has(k.email));
    if (!found.length) return;
    root.querySelector('[data-nocontacts]')?.remove();
    const fmt = (d) => d ? d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '';
    const item = (k) => personHtml({
      n: k.name || k.email, email: k.email,
      role: `${k.threads} email${k.threads === 1 ? '' : 's'}${k.last ? ` · last ${fmt(k.last)}` : ''}${k.projects.length ? ` · ${k.projects.slice(0, 3).join(', ')}${k.projects.length > 3 ? '…' : ''}` : ''}`,
    });
    const SHOW = 5;
    box.innerHTML = `<div style="padding:12px 0 4px;font-size:12px;color:${C.muted}">From email</div>
      ${found.slice(0, SHOW).map(item).join('')}
      ${found.length > SHOW ? `<div data-more hidden>${found.slice(SHOW).map(item).join('')}</div><button type="button" class="clx-link" data-moreb style="font-weight:400;font-size:12.5px;padding:8px 0">Show ${found.length - SHOW} more</button>` : ''}`;
    box.querySelector('[data-moreb]')?.addEventListener('click', (e) => { box.querySelector('[data-more]').hidden = false; e.currentTarget.remove(); });
  }

  // Edit client / Client record → the existing relationship card (the deep
  // editor: details, address, related clients, the full history).
  async function openRecordCard(c) {
    const raw = rawById.get(c.id);
    if (!raw) return;
    const { content, close } = openOverlay({
      title: raw.name || '—',
      subtitle: [schemeLabel(raw.type), raw.address].filter(Boolean).join(' · '),
      wide: true,
    });
    const rec = index[c.id] || { projects: [], enquiries: [], invoices: [] };
    const card = {
      ...raw,
      projects: rec.projects.length,
      live: rec.projects.filter((p) => ACTIVE_STATUSES.includes(p.status)).length,
      billed: rec.invoices.reduce((s, iv) => s + (Number(iv.amount) || 0), 0),
      last: null,
      similar: similarTo(raw, clients),
    };
    mountClientCard(content, close, card, rec, { canSeeEnquiries, isFinance, defaultTerms }, { session, onOpenProject, onOpenEnquiry }, clients);
  }

  // Archive ending (6g) — states what survives before it asks, then flips the
  // client to archived (reversible) and logs a Status change line.
  function openArchiveConfirm(c) {
    openEndingConfirm({
      eyebrow: 'Archive client',
      record: c.name || '—',
      whatStays: [
        'Every project, enquiry and invoice for this client',
        'Their full history stays searchable',
        'Documents and mail already filed',
      ],
      whatChanges: 'Hidden from the client list. No new work is started against them.',
      confirmLabel: 'Archive client',
      reversibleNote: 'Reversible',
      onConfirm: async () => {
        const at = new Date().toISOString();
        const by = session ? session.email : null;
        const byName = session ? session.displayName : null;
        await updateClient(c.id, { archived: true, archivedBy: by, archivedAt: at, manualOverride: true, updatedBy: by, updatedAt: at });
        await addRecordChange({ record: 'clients/' + c.id, field: 'status', label: 'Status', from: 'Active', to: 'Archived', by, byName, at });
        const i = all.findIndex((x) => x.id === c.id);
        if (i >= 0) all.splice(i, 1);
        byId.delete(c.id);
        order = order.filter((x) => x !== c.id);
        backToList();
      },
    });
  }

  injectFormStyles(); // the tf-btn primary / secondary buttons
  if (openClientId && byId.has(openClientId)) {
    order = sortClients(all.filter((c) => matches(c, LIST_STATE.q)), LIST_STATE.sort).map((c) => c.id);
    drawDetail(openClientId);
  } else {
    drawList();
  }
}
